import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Menu,
  MenuItem,
  Button,
  useTheme,
  type Theme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SearchField } from "../shared";
import { Subcircle } from "../../hooks/useSubcircles";
import type { NoticeFeedTab } from "../../utils/noticeFeedFilters";

interface NoticeFilterBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedType: string;
  onTypeChange: (value: string) => void;
  selectedCircle: string;
  onCircleChange: (value: string) => void;
  selectedSender: string;
  onSenderChange: (value: string) => void;
  activeTab: NoticeFeedTab;
  subcircles: Subcircle[];
  uniqueSenders: string[];
  filteredCount: number;
  onClearFilters: () => void;
}

/** Shared sx for all filter buttons */
const getFilterButtonSx = (isActive: boolean, theme: Theme) => {
  return {
    textTransform: "none" as const,
    fontWeight: 600,
    borderRadius: 1,
    boxShadow: "none",
    minWidth: 80,
    px: { xs: 1, sm: 2 },
    py: 0.75,
    height: 36,
    fontSize: { xs: "0.68rem", sm: "0.875rem" },
    whiteSpace: "nowrap",
    lineHeight: 1.1,
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.common.white,
    color: isActive
      ? theme.palette.common.white
      : theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    transition: theme.transitions.create(
      ["background-color", "border-color", "transform", "box-shadow"],
      { duration: theme.transitions.duration.short }
    ),
    "&:hover": {
      backgroundColor: isActive
        ? theme.palette.primary.dark
        : theme.palette.primary.light + "10",
      borderColor: theme.palette.primary.dark,
      transform: "translateY(-1px)",
      boxShadow: isActive
        ? `0 4px 12px ${theme.palette.primary.main}40`
        : `0 4px 12px ${theme.palette.primary.main}20`,
    },
    "&:active": {
      transform: "translateY(0)",
    },
  };
};

const NoticeFilterBar: React.FC<NoticeFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  selectedType,
  onTypeChange,
  selectedCircle,
  onCircleChange,
  selectedSender,
  onSenderChange,
  activeTab,
  subcircles,
  uniqueSenders,
  filteredCount,
  onClearFilters,
}) => {
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const [circleMenuAnchor, setCircleMenuAnchor] = useState<null | HTMLElement>(null);
  const [senderMenuAnchor, setSenderMenuAnchor] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const typeSx = getFilterButtonSx(selectedType !== "all", theme);
  const circleSx = getFilterButtonSx(selectedCircle !== "all", theme);
  const senderSx = getFilterButtonSx(selectedSender !== "all", theme);
  const statusLabel = activeTab === "scheduled" ? "Scheduled" : "Sent";

  const hasActiveFilters =
    selectedCircle !== "all" ||
    selectedType !== "all" ||
    selectedSender !== "all" ||
    searchQuery !== "";

  const typeLabel =
    selectedType === "all"
      ? "All Types"
      : selectedType === "info"
      ? "Info"
      : selectedType === "urgent"
      ? "Urgent"
      : selectedType === "image"
      ? "Image"
      : "Events";

  const circleLabel =
    selectedCircle === "all"
      ? "All Circles"
      : selectedCircle === "main"
      ? "Main Circle"
      : subcircles.find((s) => s.circleId === selectedCircle)?.name || "Circle";

  const senderLabel =
    selectedSender === "all" ? "All Senders" : selectedSender;

  return (
    <>
      {/* Search */}
      <Box sx={{ mb: 2.5 }}>
        <SearchField
          value={searchQuery}
          onChange={onSearchChange}
          placeholder="Search notices..."
          sx={{ width: "100%" }}
          data-testid="view-notices-search-input"
        />
      </Box>

      {/* Filter Buttons */}
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            flexWrap: "nowrap",
            gap: 0.75,
            mb: 0.5,
            width: "100%",
          }}
        >
          {/* Type Filter */}
          <Button
            variant="contained"
            onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            data-testid="view-notices-type-filter-button"
            sx={{ ...typeSx, flex: 1, minWidth: 0 }}
          >
            {typeLabel}
          </Button>

          {/* Circle Filter */}
          <Button
            variant="contained"
            onClick={(e) => setCircleMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            data-testid="view-notices-circle-filter-button"
            sx={{ ...circleSx, flex: 1, minWidth: 0 }}
          >
            {circleLabel}
          </Button>

          {/* Sender Filter */}
          <Button
            variant="contained"
            onClick={(e) => setSenderMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            data-testid="view-notices-sender-filter-button"
            sx={{ ...senderSx, flex: 1, minWidth: 0 }}
          >
            {senderLabel}
          </Button>

          {/* Status (read-only — driven by tab) */}
          <Button
            variant="contained"
            disabled
            sx={{
              ...getFilterButtonSx(true, theme),
              flex: 1,
              minWidth: 0,
              opacity: 1,
              "&.Mui-disabled": {
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            Status: {statusLabel}
          </Button>
        </Stack>

        {/* Filter Results Summary */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {hasActiveFilters && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {filteredCount} notice{filteredCount !== 1 ? "s" : ""} found
            </Typography>
          )}
          {hasActiveFilters && (
            <Chip
              label="Clear filters"
              variant="outlined"
              size="small"
              onClick={onClearFilters}
              sx={{ cursor: "pointer", fontSize: "0.75rem", ml: "auto" }}
              data-testid="view-notices-clear-filters"
            />
          )}
        </Box>
      </Box>

      {/* ── Dropdown Menus ────────────────────────────────── */}

      {/* Type Menu */}
      <Menu
        anchorEl={typeMenuAnchor}
        open={Boolean(typeMenuAnchor)}
        onClose={() => setTypeMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}
      >
        {["all", "info", "urgent", "event", "image"].map((type) => (
          <MenuItem
            key={type}
            onClick={() => {
              onTypeChange(type);
              setTypeMenuAnchor(null);
            }}
          >
            {type === "all"
              ? "All Types"
              : type === "info"
              ? "Info"
              : type === "urgent"
              ? "Urgent"
              : type === "event"
              ? "Events"
              : "Image"}
          </MenuItem>
        ))}
      </Menu>

      {/* Circle Menu */}
      <Menu
        anchorEl={circleMenuAnchor}
        open={Boolean(circleMenuAnchor)}
        onClose={() => setCircleMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1, maxHeight: 300 } }}
      >
        <MenuItem
          onClick={() => {
            onCircleChange("all");
            setCircleMenuAnchor(null);
          }}
        >
          All Circles
        </MenuItem>
        <MenuItem
          onClick={() => {
            onCircleChange("main");
            setCircleMenuAnchor(null);
          }}
        >
          Main Circle
        </MenuItem>
        {subcircles.map((subcircle) => (
          <MenuItem
            key={subcircle.circleId}
            onClick={() => {
              onCircleChange(subcircle.circleId);
              setCircleMenuAnchor(null);
            }}
          >
            {subcircle.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Sender Menu */}
      <Menu
        anchorEl={senderMenuAnchor}
        open={Boolean(senderMenuAnchor)}
        onClose={() => setSenderMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, mt: 1, maxHeight: 300 } }}
      >
        <MenuItem
          onClick={() => {
            onSenderChange("all");
            setSenderMenuAnchor(null);
          }}
        >
          All Senders
        </MenuItem>
        {uniqueSenders.map((sender) => (
          <MenuItem
            key={sender}
            onClick={() => {
              onSenderChange(sender);
              setSenderMenuAnchor(null);
            }}
          >
            {sender}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NoticeFilterBar;
